import { QueryCache, QueryClient } from '@tanstack/react-query';
import {
  createTRPCClient,
  httpBatchLink,
  httpSubscriptionLink,
  splitLink,
} from '@trpc/client';
import { createTRPCOptionsProxy } from '@trpc/tanstack-react-query';
import {
  EventSourcePolyfill,
  type EventSourcePolyfillInit,
} from 'event-source-polyfill';
import { toast } from 'sonner';
import type { AppRouter } from '../lib/trpc/router';
import { fetchAuthSession } from 'aws-amplify/auth';

const baseUrl = import.meta.env.VITE_MAINAPP_API_BASE_URL
  ? `${import.meta.env.VITE_MAINAPP_API_BASE_URL}/api/v1`
  : 'http://localhost:2022';

const getAuthHeaders = async () => {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.accessToken?.toString();
    return { Authorization: token ? `Bearer ${token}` : '' };
  } catch (error) {
    console.error('Error fetching auth session:', error);
    return {};
  }
};

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (_error, query) => {
      const errorMessage = query.meta?.errorMessage as string | undefined;
      if (errorMessage) {
        toast.error(errorMessage);
      }
    },
  }),
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

const trpcClient = createTRPCClient<AppRouter>({
  links: [
    splitLink({
      condition: (op) => op.type === 'subscription',
      true: httpSubscriptionLink({
        url: baseUrl,
        EventSource: EventSourcePolyfill,
        eventSourceOptions: async () => {
          const headers = await getAuthHeaders();
          return {
            headers,
            heartbeatTimeout: Number.MAX_SAFE_INTEGER, // Disable 45 second (default) heartbeat to prevent premature disconnections
          } as EventSourcePolyfillInit;
        },
      }),
      false: splitLink({
        condition: (op) => op.path === 'patientsRouter.searchPatients',
        true: httpBatchLink({
          url: baseUrl,
          headers: getAuthHeaders,
          methodOverride: 'POST',
        }),
        false: httpBatchLink({
          url: baseUrl,
          headers: getAuthHeaders,
        }),
      }),
    }),
  ],
});

export const trpc = createTRPCOptionsProxy<AppRouter>({
  client: trpcClient,
  queryClient,
});
